import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, DownloadSimple } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

const ROLE_LABEL = { admin: "Administrateur", professeur: "Professeur", etudiant: "Étudiant" };
const ROLE_COLOR = { admin: "bg-accent", professeur: "bg-primary", etudiant: "bg-sky-600" };
const EMPTY = { name: "", username: "", email: "", password: "", role: "etudiant", filiere: "", promotion: "", semestre: "" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [academic, setAcademic] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/academic").then((r) => setAcademic(r.data));
  };
  useEffect(() => { load(); }, []);

  const filieres = academic.filter((a) => a.type === "filiere");
  const selectedFiliere = filieres.find((f) => f.name === form.filiere);
  const promotions = academic.filter((a) => a.type === "promotion" && (!selectedFiliere || a.parent === selectedFiliere.id));
  const selectedPromotion = promotions.find((p) => p.name === form.promotion);
  const semestres = academic.filter((a) => a.type === "semestre" && (!selectedPromotion || a.parent === selectedPromotion.id));

  const setFiliere = (name) => setForm((f) => ({ ...f, filiere: name, promotion: "", semestre: "" }));
  const setPromotion = (name) => setForm((f) => ({ ...f, promotion: name, semestre: "" }));

  const save = async () => {
    try {
      if (editId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editId}`, payload);
        toast.success("Utilisateur modifié");
      } else {
        await api.post("/users", form);
        toast.success("Utilisateur créé");
      }
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const edit = (u) => { setForm({ name: u.name, username: u.username || "", email: u.email, password: "", role: u.role, filiere: u.filiere || "", promotion: u.promotion || "", semestre: u.semestre || "" }); setEditId(u.id); setOpen(true); };
  const del = async (id) => { try { await api.delete(`/users/${id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(apiError(e)); } };
  const downloadTranscript = async (u) => {
    try {
      const res = await api.get(`/users/${u.id}/grades/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = `releve-${u.name}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const shown = filter === "all" ? users : users.filter((u) => u.role === filter);

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Administration</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Utilisateurs</h1>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les rôles</SelectItem>
              <SelectItem value="admin">Administrateurs</SelectItem>
              <SelectItem value="professeur">Professeurs</SelectItem>
              <SelectItem value="etudiant">Étudiants</SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
            <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-user-btn"><Plus size={18} className="mr-2" /> Ajouter</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editId ? "Modifier" : "Nouvel"} utilisateur</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nom complet</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
                <div><Label>Identifiant (connexion)</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} autoCapitalize="none" autoCorrect="off" data-testid="user-username" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
                <div><Label>Mot de passe {editId && "(laisser vide pour ne pas changer)"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
                <div>
                  <Label>Rôle</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="etudiant">Étudiant</SelectItem>
                      <SelectItem value="professeur">Professeur</SelectItem>
                      <SelectItem value="admin">Administrateur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label>Filière</Label>
                    <Select value={form.filiere || "__none"} onValueChange={(v) => setFiliere(v === "__none" ? "" : v)}>
                      <SelectTrigger data-testid="user-filiere"><SelectValue placeholder="Aucune" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Aucune</SelectItem>
                        {filieres.map((f) => <SelectItem key={f.id} value={f.name}>{f.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {filieres.length === 0 && <p className="mt-1 text-xs text-muted-foreground">Aucune filière définie — voir Gestion académique.</p>}
                  </div>
                  <div className="flex-1">
                    <Label>Promotion</Label>
                    <Select value={form.promotion || "__none"} onValueChange={(v) => setPromotion(v === "__none" ? "" : v)} disabled={!selectedFiliere}>
                      <SelectTrigger data-testid="user-promotion"><SelectValue placeholder={selectedFiliere ? "Aucune" : "Choisir une filière d'abord"} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Aucune</SelectItem>
                        {promotions.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Semestre</Label>
                  <Select value={form.semestre || "__none"} onValueChange={(v) => setForm({ ...form, semestre: v === "__none" ? "" : v })} disabled={!selectedPromotion}>
                    <SelectTrigger data-testid="user-semestre"><SelectValue placeholder={selectedPromotion ? "Aucun" : "Choisir une promotion d'abord"} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Aucun</SelectItem>
                      {semestres.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-user-btn">{editId ? "Enregistrer" : "Créer"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Vue carte (mobile) */}
      <div className="space-y-3 md:hidden">
        {shown.map((u) => (
          <div key={u.id} className="rounded-xl border border-border bg-card p-4" data-testid={`user-card-${u.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-medium">{u.name}</p>
                <p className="truncate text-sm text-muted-foreground">{u.email}</p>
              </div>
              <Badge className={`shrink-0 ${ROLE_COLOR[u.role]} hover:${ROLE_COLOR[u.role]}`}>{ROLE_LABEL[u.role]}</Badge>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>Identifiant : {u.username || "—"}</span>
              <span>Filière : {u.filiere || "—"}</span>
            </div>
            <div className="mt-3 flex justify-end gap-1 border-t border-border pt-3">
              <Button size="icon" variant="ghost" onClick={() => edit(u)} data-testid={`edit-user-mobile-${u.id}`}><PencilSimple size={16} /></Button>
              {u.role === "etudiant" && <Button size="icon" variant="ghost" onClick={() => downloadTranscript(u)} data-testid={`transcript-user-mobile-${u.id}`}><DownloadSimple size={16} /></Button>}
              <Button size="icon" variant="ghost" onClick={() => del(u.id)} data-testid={`del-user-mobile-${u.id}`}><Trash size={16} className="text-accent" /></Button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">Aucun utilisateur.</p>}
      </div>

      {/* Vue tableau (desktop) */}
      <div className="hidden rounded-xl border border-border bg-card md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Identifiant</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Rôle</TableHead>
              <TableHead>Filière</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-muted-foreground">{u.username || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell><Badge className={`${ROLE_COLOR[u.role]} hover:${ROLE_COLOR[u.role]}`}>{ROLE_LABEL[u.role]}</Badge></TableCell>
                <TableCell className="text-muted-foreground">{u.filiere || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => edit(u)} data-testid={`edit-user-${u.id}`}><PencilSimple size={16} /></Button>
                  {u.role === "etudiant" && <Button size="icon" variant="ghost" onClick={() => downloadTranscript(u)} data-testid={`transcript-user-${u.id}`}><DownloadSimple size={16} /></Button>}
                  <Button size="icon" variant="ghost" onClick={() => del(u.id)} data-testid={`del-user-${u.id}`}><Trash size={16} className="text-accent" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
